<template>
  <content-modal
    ref="stpTooltipModal"
    :visible="visible"
    :close-aria-label="closeAriaLabel"
    :size="size"
    :auto-hide-off="autoHideOff"
    class="stp-tooltip-modal"
    @modal-hide-request="hideRequest"
  >
    <template #title>{{ t("stp_modal_title") }}</template>
    <template #content>
      <modal-content>
        <template #content>
          <stp-tooltip-settings
            :task="task"
            :selected-stp="params.selectedStp"
            @is-dirty="handleIsDirty"
            @validated="handleValidated"
            @save-data="handleSaveData"
          />
        </template>
        <template #tools>
          <cv-button class="cancel" kind="secondary" size="field" @click="handleExitAction">
            {{ t("button_cancel") }}
          </cv-button>
          <fetch-data-button
            kind="primary"
            size="field"
            :loading="processingState.isLoading"
            :error="processingState.error"
            :error-message="processingState.errorMessage"
            :tip-position="dataSaveBtnTipPosition"
            @click="handleSave"
          >
            <span v-if="processingState.error">{{ t("button_state_error") }}</span>
            <span v-else>{{ primaryButtonLabel }}</span>
          </fetch-data-button>
        </template>
      </modal-content>
    </template>
  </content-modal>
</template>

<script setup lang="ts">
import DOMUtils from "@utils/dom.utils";
import { notDefined } from "@utils/var.check";
import stpEventBus from "@plugins/richtext/plugins/stp/service/event-bus";
import EventBusUtils from "@plugins/common/service/event.bus.utils";
import { ref, computed, useTemplateRef, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";
import { useModal } from "@plugins/modal/composables/modal-instance.ts";

// Props
const props = defineProps({
  params: { type: Object, required: true },
});

const { t } = useI18n();
const modal = useModal();

// State
const closeAriaLabel = ref("Close");
const size = ref("medium");
const autoHideOff = ref(true);
const visible = ref(true);
const currentAction = ref("");
const task = ref("");
const dataSaveBtnTipPosition = ref("top");

const processingState = ref({
  isLoading: false,
  error: false,
  errorMessage: "",
});

const stpTooltipModal = useTemplateRef("stpTooltipModal");

// Computed
const primaryButtonLabel = computed(() =>
  notDefined(props.params.selectedStp) ? t("button_add") : t("button_save"),
);

// Methods
const isProcessing = computed(() => processingState.value.isLoading);

const setProcessing = () => {
  processingState.value.isLoading = true;
};

const setProcessingError = () => {
  processingState.value.isLoading = false;
  processingState.value.error = true;
  // short delay before removing the error state.
  setTimeout(resetProcessingState, 3000);
};

const resetProcessingState = () => {
  processingState.value.isLoading = false;
  processingState.value.error = false;
};

const doCheckDirty = () => {
  task.value = "checkDirty";
};
const doValidate = () => {
  task.value = "validate";
};
const doSave = () => {
  task.value = "save";
};
const resetTask = () => {
  task.value = "";
};

const handleIsDirty = (isDirty: boolean) => {
  if (!isDirty) {
    hide();
    return;
  }

  if (currentAction.value === "save") {
    // proceed to next step, validate
    doValidate();
  } else if (currentAction.value === "exit") {
    resetTask();
    promptPageDirty();
  }
};

const handleValidated = (result) => {
  if (result.code === "ok") {
    // validate successful, proceed to saving step.
    doSave();
  } else {
    resetTask();
    setProcessingError();
  }
};

const handleSaveData = (result) => {
  if (result.code === "ok") {
    stpEventBus.$emit("addStp", result.data);
  } else {
    resetTask();
    setProcessingError();
  }
};

const handleSave = () => {
  // do nothing when clicking on upload button while still uploading
  if (isProcessing.value) return;

  setProcessing();
  currentAction.value = "save";
  // We will not check dirty for save due to the patching of legacy data
  doValidate();
};

const handleExitAction = () => {
  currentAction.value = "exit";
  setProcessing();
  doCheckDirty();
};

const promptPageDirty = () => {
  if (confirm(t("dirty_confirmation_prompt"))) {
    // discard changes, close modal
    hide();
  } else {
    // don't exit, continue edit
    currentAction.value = "";
    resetProcessingState();
  }
};

const hide = () => {
  EventBusUtils.unWatchInstance(props.params.eventBusWatchInstances);
  // restore previous scrollability
  DOMUtils.removeClass("body", "bx--body--with-modal-open");
  modal.remove(props.params.modalId);
};

const hideRequest = (event) => {
  // to prevent close modal upon clicking anywhere outside of the modal container
  if (event["cv:reason"] !== "external-click") {
    handleExitAction();
  }
};

onUnmounted(() => {
  hide();
});
</script>
